import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Download, X, CheckCircle2, Loader2 } from 'lucide-react';
import { trackLead } from '@/lib/metaPixel';

const API = process.env.REACT_APP_BACKEND_URL;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// In-app browsers (Instagram, Facebook, etc.) block blob downloads.
// We detect them and fall back to a direct URL navigation.
function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Instagram|FBAN|FBAV|FB_IAB|FBIOS|Line\//i.test(ua);
}

export default function LeadModal({ open, onClose }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [inApp, setInApp] = useState(false);

  useEffect(() => { setInApp(isInAppBrowser()); }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // Reset state when closed
  useEffect(() => {
    if (!open) {
      setEmail('');
      setLoading(false);
      setSuccess(false);
    }
  }, [open]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!EMAIL_RE.test(cleaned)) {
      toast.error('Ingresá un email válido');
      return;
    }

    // In-app browser fallback: blob download is blocked.
    // Navigate the page directly to the GET endpoint (which also registers the lead + fires CAPI server-side).
    if (inApp) {
      try {
        // Fire client-side Pixel Lead before leaving the page (best effort, deduped server-side).
        trackLead();
      } catch (_) { /* never block */ }
      const url = `${API}/api/lead?email=${encodeURIComponent(cleaned)}`;
      window.location.href = url;
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: cleaned }),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || 'No pudimos generar la descarga');
      }

      const eventId = res.headers.get('X-Lead-Event-Id') || undefined;
      const blob = await res.blob();

      try { trackLead({ event_id: eventId }); } catch (_) { /* never block */ }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'La-entrevista-inicial-en-una-hoja.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(true);
      toast.success('¡Listo! Empezó la descarga.');
    } catch (err) {
      toast.error(err.message || 'Error al descargar la guía');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={() => !loading && onClose?.()}
      data-testid="lead-modal-overlay"
    >
      <div
        className="relative w-full max-w-md bg-[#0D1B2A] border border-white/10 rounded-2xl shadow-2xl p-7 sm:p-10"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="lead-modal"
      >
        <button
          type="button"
          onClick={() => !loading && onClose?.()}
          disabled={loading}
          aria-label="Cerrar"
          className="absolute top-3 right-3 text-slate-400 hover:text-white p-1 rounded-md disabled:opacity-50 transition-colors"
          data-testid="lead-modal-close"
        >
          <X className="h-5 w-5" />
        </button>

        <h2
          className="text-xl sm:text-2xl font-bold text-white leading-tight tracking-tight"
          data-testid="lead-modal-title"
        >
          La entrevista inicial en una hoja
        </h2>

        <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">
          Las 3 preguntas del criterio ACSM y las 9 señales de alarma, para tener a mano en tu próxima entrevista.
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="lead-modal-form">
          <Input
            id="lead-modal-email"
            type="email"
            required
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tunombre@email.com"
            disabled={loading}
            data-testid="lead-modal-email-input"
            className="h-14 text-base bg-[#0B1220] border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-[#5B9B2F] focus-visible:border-[#5B9B2F]"
          />

          <Button
            type="submit"
            size="lg"
            disabled={loading}
            data-testid="lead-modal-submit"
            className="w-full h-14 bg-[#5B9B2F] hover:bg-[#4a8126] text-white font-bold text-base rounded-xl shadow-lg shadow-[#5B9B2F]/25 transition-all hover:scale-[1.01] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Descargando...
              </>
            ) : success ? (
              <>
                <CheckCircle2 className="h-5 w-5 mr-2" />
                Descargar de nuevo
              </>
            ) : (
              <>
                <Download className="h-5 w-5 mr-2" />
                Descargar la guía
              </>
            )}
          </Button>

          <p className="text-[11px] text-slate-500 leading-relaxed text-center" data-testid="lead-modal-consent">
            Al dejar tu email aceptás recibir material sobre entrevista inicial y ejercicio. Podés darte de baja cuando quieras.
          </p>
        </form>

        {success && (
          <p
            className="mt-5 text-sm text-[#5B9B2F] text-center font-medium"
            data-testid="lead-modal-success"
          >
            ✓ Listo. Empezó la descarga y te la mandamos por mail.
          </p>
        )}
      </div>
    </div>
  );
}
